/* eslint-disable */ // Prop types são definidos no final
import React, { useMemo, useContext, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box, Select, MenuItem, FormControl } from "@material-ui/core";
import initiativeManagementStyle from "assets/jss/material-dashboard-pro-react/views/initiativesManagementStyle.js";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { Doughnut } from "react-chartjs-2";
import Tippy from "@tippyjs/react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formatNumber } from "utils/formatNumberUtils";
import "utils/chartjs-setup";
import { getProgressColor } from "utils/thresholdUtils";
import AuthContext from "contexts/AuthProvider";
import { TrendingUp } from "@material-ui/icons";
import { getAltLabel } from "utils/getAltLabel";

// Registrar ArcElement para Doughnut
ChartJS.register(ArcElement);

// --- Funções Utilitárias para Cores ---
function generateColorPalette(baseColor) {
  // Validação da cor base
  if (!baseColor || !/^#[0-9A-F]{6}$/i.test(baseColor)) {
    baseColor = "#4A90E2"; // Fallback para azul padrão
  }

  // Remove o # e converte para RGB
  const hex = baseColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Gera variações da cor base
  const primary = baseColor;
  const primaryLight = `rgba(${r}, ${g}, ${b}, 0.1)`;
  const primaryMedium = `rgba(${r}, ${g}, ${b}, 0.6)`;
  const primaryDark = `rgba(${r}, ${g}, ${b}, 0.8)`;
  
  // Cor complementar para contraste (versão suavizada)
  const complementary = `hsl(${(r * 0.299 + g * 0.587 + b * 0.114) > 128 ? 0 : 180}, 50%, 70%)`;
  
  // Cores neutras modernas
  const neutral = {
    50: "#FAFAFA",
    100: "#F5F5F5", 
    200: "#EEEEEE",
    300: "#E0E0E0",
    400: "#BDBDBD",
    500: "#9E9E9E",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121"
  };

  return {
    primary,
    primaryLight,
    primaryMedium,
    primaryDark,
    complementary,
    neutral
  };
}

function pastelColor(baseColor) {
  switch (baseColor) {
    case "#4caf50":
      return "#C8E6C9";
    case "#ffeb3b":
      return "#FFF9C4";
    case "#f44336":
      return "#FFCDD2";
    default:
      return "#fff";
  }
}

// --- Estilos Modernizados e Minimalistas ---
const useStyles = (clientColor) => makeStyles((theme) => ({
  // Estilos do filtro seguindo o padrão da aplicação
  ...initiativeManagementStyle(theme, clientColor),
  selectFormControlOverride: {
    margin: "0 !important",
    minWidth: "120px !important",
    "& .MuiSelect-select": {
      fontSize: "0.7rem !important",
      padding: "4px 8px !important",
    },
  },
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: "8px",
  },
  filtersWrapper: {
    display: "flex",
    gap: "6px",
    marginBottom: "8px",
    alignItems: "center",
  },
  contentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
    alignItems: "center",
    gap: "12px",
  },
  chartSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    minHeight: "200px",
  },
  chartArea: {
    flex: 1,
    position: "relative",
    width: "100%",
    height: "100%",
  },
  gaugeSection: {
    width: "100px",
    flexShrink: 0,
  },
  gaugeBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "12px 10px 10px 10px",
    borderRadius: "12px",
    backgroundColor: "#fafafa",
    border: "0.5px solid #e0e0e0",
    gap: "8px",
    marginTop: "-70px",
  },
  performanceLabel: {
    fontSize: "0.6rem",
    fontWeight: 600,
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    textAlign: "center",
    marginBottom: "-15px",
  },
  gaugeContainer: {
    width: "65px",
    height: "65px",
    position: "relative",
  },
  performanceValue: {
    fontSize: "1.2rem",
    fontWeight: 700,
    textAlign: "center",
    marginTop: "-25px",
  },
  tableContainer: {
    flex: 1,
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: "calc(100% - 16px)",
  },
  kpiTable: {
    borderCollapse: "collapse",
    width: "100%",
    fontSize: "0.7rem",
    color: "#374151",
    textAlign: "center",
  },
  kpiTableHeaderCell: {
    padding: "6px 8px",
    borderBottom: "2px solid #e0e0e0",
    backgroundColor: "#fafafa",
    fontWeight: 600,
    color: "#666",
    fontSize: "0.65rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
  },
  kpiTableCell: {
    padding: "5px 8px",
    borderBottom: "1px solid #f0f0f0",
    fontSize: "0.7rem",
    whiteSpace: "nowrap",
  },
  rowHeader: {
    textAlign: "left",
    fontWeight: 600,
    backgroundColor: "#fafafa",
    color: "#333",
    fontSize: "0.7rem",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: "100%",
    padding: "32px 16px",
    textAlign: "center",
    position: "absolute",
    top: 0,
    left: 0,
  },
  emptyStateIcon: {
    fontSize: "48px",
    color: "#e0e0e0",
    marginBottom: "16px",
  },
  emptyStateTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#666",
    marginBottom: "8px",
  },
  emptyStateDescription: {
    fontSize: "0.75rem",
    color: "#999",
    lineHeight: 1.5,
    maxWidth: "280px",
  },
}));

// --- Componente Principal Modernizado ---
function MixedBarLineChart({
  timeAgg,
  viewMode,
  data,
  trackingMode,
  thresholdsConfig,
  unit = "",
  showUnitBefore = false,
  rawData = [], // Dados brutos das iniciativas para filtros
  showFilters = false, // Prop opcional para exibir filtros
  selectedBranch: externalSelectedBranch,
  selectedWorkfront: externalSelectedWorkfront,
  onBranchChange,
  onWorkfrontChange,
}) {
  const { t } = useTranslation();
  const { userDetails } = useContext(AuthContext);
  
  // Obtém as cores da implementação do usuário
  const clientColor = userDetails?.implementation_color || "#4A90E2";
  const clientColor2 = userDetails?.implementation_color2;
  const clientColor3 = userDetails?.implementation_color3;
  
  const classes = useStyles(clientColor)();
  
  const colorPalette = useMemo(() => generateColorPalette(clientColor), [clientColor]);

  // Estados internos para filtros (usado quando não há callbacks externos)
  const [internalSelectedBranch, setInternalSelectedBranch] = useState("");
  const [internalSelectedWorkfront, setInternalSelectedWorkfront] = useState("");
  
  // Determina se usar estado externo (controlado) ou interno
  const isControlled = onBranchChange !== undefined || onWorkfrontChange !== undefined;
  const selectedBranch = isControlled ? (externalSelectedBranch || "") : internalSelectedBranch;
  const selectedWorkfront = isControlled ? (externalSelectedWorkfront || "") : internalSelectedWorkfront;
  
  const handleBranchChange = (value) => {
    if (onBranchChange) {
      onBranchChange(value);
    } else {
      setInternalSelectedBranch(value);
    }
  };
  
  const handleWorkfrontChange = (value) => {
    if (onWorkfrontChange) {
      onWorkfrontChange(value);
    } else {
      setInternalSelectedWorkfront(value);
    }
  };

  // Extrair branches e workfronts únicos dos dados brutos
  const { branches, workfronts } = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return { branches: [], workfronts: [] };
    }
    
    const branchSet = new Set();
    const workfrontSet = new Set();
    
    rawData.forEach(item => {
      if (item.branch) branchSet.add(item.branch);
      if (item.workfront) workfrontSet.add(item.workfront);
    });
    
    return {
      branches: Array.from(branchSet).sort(),
      workfronts: Array.from(workfrontSet).sort(),
    };
  }, [rawData]);

  // Dados para o gráfico - a filtragem agora é feita no backend
  // Os dados já vêm filtrados quando selectedBranch ou selectedWorkfront mudam
  // pois o componente pai refaz a chamada à API com os filtros
  const filteredData = data;

  // --- Processamento dos Dados com Cores Dinâmicas ---
  const { chartData, chartOptions } = useMemo(() => {
    const source = filteredData;
    if (
      !source ||
      !source.labels ||
      !source.series ||
      source.series.length < 2
    ) {
      return { chartData: { labels: [], datasets: [] }, chartOptions: {} };
    }

    // Encontrar o índice da série "Real" pelo nome
    const seriesNames = source.seriesNames || [];
    const realIndex = seriesNames.findIndex(name => name === "Real");
    
    // Se não encontrou "Real", assumir que é a última série (compatibilidade com dados antigos)
    const realIdx = realIndex !== -1 ? realIndex : 1;

    // Mapear cores específicas para cada série com fallback
    const seriesColors = {
      "Meta Inicial": clientColor, // color (baseline)
      "Meta": clientColor2 || colorPalette.primaryDark, // color2 ou fallback
      "Real": clientColor3 || colorPalette.complementary, // color3 ou fallback
    };

    // Criar datasets dinamicamente
    const datasets = [];
    
    // Primeiro, adicionar todas as séries que não são "Real" como linhas
    seriesNames.forEach((name, index) => {
      if (index !== realIdx) {
        // Usar cor específica ou fallback para cor primária
        const lineColor = seriesColors[name] || colorPalette.primary;
        
        datasets.push({
          type: "line",
          label: name,
          data: source.series[index],
          borderColor: lineColor,
          backgroundColor: `${lineColor}20`, // 20 = transparência em hex
          borderWidth: 3,
          pointBackgroundColor: lineColor,
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.2,
          yAxisID: "y",
          fill: false,
        });
      }
    });

    // Depois, adicionar a série "Real" como barra
    const realColor = seriesColors["Real"];
    datasets.push({
      type: "bar",
      label: seriesNames[realIdx] || "Real",
      data: source.series[realIdx],
      backgroundColor: realColor,
      borderColor: realColor,
      borderWidth: 0,
      barPercentage: 0.7,
      categoryPercentage: 0.85,
      yAxisID: "y",
      borderRadius: 4,
    });

    const chartJsData = {
      labels: source.labels,
      datasets: datasets,
    };

    // --- Opções do Gráfico Modernizadas ---
    const options = {
      responsive: true,
      maintainAspectRatio: false,
             animation: {
         duration: 1000,
         easing: "easeOutQuart",
       },
      plugins: {
        legend: {
          position: "bottom",
          align: "center",
          labels: {
            usePointStyle: true,
            boxWidth: 10,
            padding: 20,
            font: {
              size: 12,
              weight: "500",
            },
            color: colorPalette.neutral[700],
          },
        },
        tooltip: {
          enabled: true,
          mode: "index",
          intersect: false,
          backgroundColor: "#FFFFFF",
          titleColor: colorPalette.neutral[900],
          bodyColor: colorPalette.neutral[700],
          borderColor: colorPalette.neutral[200],
          borderWidth: 1,
          padding: 16,
          cornerRadius: 8,
          usePointStyle: true,
          titleFont: {
            size: 13,
            weight: "600",
          },
          bodyFont: {
            size: 12,
          },
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || "";
              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += formatNumber(context.parsed.y, unit, showUnitBefore);
              }
              return label;
            },
            afterBody: function (context) {
              // Encontrar items de Meta e Real pelo label do dataset
              const metaItem = context.find((item) => item.dataset.label === "Meta");
              const realItem = context.find((item) => item.dataset.label === "Real");

              if (
                metaItem &&
                realItem &&
                metaItem.parsed.y > 0 &&
                realItem.parsed.y !== null
              ) {
                const metaVal = metaItem.parsed.y;
                const realVal = realItem.parsed.y;
                const ratio = ((realVal / metaVal) * 100).toFixed(1);
                return `\n${t("whatshappening.tooltip.real_meta_ratio")} ${ratio}%`;
              }
              return "";
            },
          },
        },
        title: {
          display: false,
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: false,
          },
          grid: {
            display: true,
            color: colorPalette.neutral[100],
            lineWidth: 0.7,
            drawBorder: false,
          },
          ticks: {
            font: {
              size: 11,
              weight: "500",
            },
            color: colorPalette.neutral[400],
            padding: 1,
          },
        },
        y: {
          display: true,
          position: "left",
          title: {
            display: false,
          },
          grid: {
            display: true,
            color: colorPalette.neutral[100],
            lineWidth: 0.5,
            drawBorder: false,
          },
          ticks: {
            font: {
              size: 11,
              weight: "500",
            },
            color: colorPalette.neutral[400],
            padding: 8,
            callback: function(value) {
              return formatNumber(value, unit, showUnitBefore);
            }
          },
          beginAtZero: true,
        },
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
      elements: {
        point: {
          hoverRadius: 8,
          hoverBorderWidth: 3,
        },
      },
    };

    return { chartData: chartJsData, chartOptions: options };
  }, [filteredData, t, colorPalette, unit, showUnitBefore, clientColor, clientColor2, clientColor3]);

  // --- Cálculo de Atingimento ---
  const { atingimento, progressColor, metaVal, realVal } = useMemo(() => {
    const seriesNames = filteredData?.seriesNames || [];
    const series = filteredData?.series || [];
    
    // Encontrar índices de "Meta" e "Real"
    const metaIndex = seriesNames.findIndex(name => name === "Meta");
    const realIndex = seriesNames.findIndex(name => name === "Real");
    
    // Usar índices padrão se não encontrar (compatibilidade com dados antigos)
    const metaIdx = metaIndex !== -1 ? metaIndex : 0;
    const realIdx = realIndex !== -1 ? realIndex : 1;
    
    const realArr = series[realIdx] || [];
    const metaArr = series[metaIdx] || [];
    let ratio = 0;
    let rVal = 0;
    let mVal = 0;
    let foundIndex = -1;

    for (let i = Math.min(metaArr.length, realArr.length) - 1; i >= 0; i--) {
      const mv = metaArr[i];
      const rv = realArr[i];
      if (
        mv !== null &&
        mv !== undefined &&
        mv > 0 &&
        rv !== null &&
        rv !== undefined
      ) {
        foundIndex = i;
        break;
      }
    }

    if (foundIndex !== -1) {
      rVal = realArr[foundIndex];
      mVal = metaArr[foundIndex];
      if (mVal > 0) {
        ratio = rVal / mVal;
      }
    }

    const ating = Math.round(ratio * 100);
    const pColor = getProgressColor(ratio, trackingMode, thresholdsConfig);

    return {
      atingimento: ating,
      progressColor: pColor,
      metaVal: mVal,
      realVal: rVal,
    };
  }, [filteredData, trackingMode, thresholdsConfig]);

  // --- Renderização dos Filtros ---
  const renderFilters = () => {
    // Não mostrar filtros se a prop showFilters for false
    if (!showFilters) {
      return null;
    }
    
    // Não mostrar filtros se não há dados brutos ou se há poucos dados
    if (!rawData || rawData.length === 0 || (branches.length === 0 && workfronts.length === 0)) {
      return null;
    }

    return (
      <div className={classes.filtersWrapper}>
        {/* Filtro de Branch */}
        {branches.length > 0 && (
          <FormControl
            className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
          >
            <Select
              value={selectedBranch}
              onChange={(e) => handleBranchChange(e.target.value)}
              displayEmpty
              classes={{
                select: `${classes.select} ${classes.selectFormControlOverride}`,
              }}
            >
              <MenuItem
                value=""
                classes={{
                  root: classes.selectMenuItem,
                  selected: classes.selectMenuItemSelected,
                }}
              >
                {getAltLabel("filters.all_branches", userDetails, t)}
              </MenuItem>
              {branches.map((branch) => (
                <MenuItem
                  key={branch}
                  value={branch}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {branch}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Filtro de Workfront */}
        {workfronts.length > 0 && (
          <FormControl
            className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
          >
            <Select
              value={selectedWorkfront}
              onChange={(e) => handleWorkfrontChange(e.target.value)}
              displayEmpty
              classes={{
                select: `${classes.select} ${classes.selectFormControlOverride}`,
              }}
            >
              <MenuItem
                value=""
                classes={{
                  root: classes.selectMenuItem,
                  selected: classes.selectMenuItemSelected,
                }}
              >
                {getAltLabel("filters.all_workfronts", userDetails, t)}
              </MenuItem>
              {workfronts.map((wf) => (
                <MenuItem
                  key={wf}
                  value={wf}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {wf}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>
    );
  };

  // --- Renderização do Empty State ---
  function renderEmptyState() {
    return (
      <div className={classes.emptyState}>
        <TrendingUp className={classes.emptyStateIcon} />
        <Typography className={classes.emptyStateTitle}>
          {t("whatshappening.empty.savings_chart_title")}
        </Typography>
        <Typography className={classes.emptyStateDescription}>
          {t("whatshappening.empty.savings_chart_description")}
        </Typography>
      </div>
    );
  }

  // --- Renderização do Gráfico ---
  function renderChart() {
    if (
      !chartData ||
      !chartData.datasets ||
      chartData.datasets.length === 0 ||
      chartData.labels.length === 0
    ) {
      return renderEmptyState();
    }
    
    return (
      <div className={classes.chartSection}>
        <div className={classes.chartArea}>
          <Chart
            key={`${timeAgg}-${viewMode}`}
            type="bar"
            data={chartData}
            options={chartOptions}
          />
        </div>
      </div>
    );
  }

  // --- Renderização do Gauge Moderno (Doughnut) ---
  function renderGauge() {
    if (
      !chartData ||
      !chartData.datasets ||
      chartData.datasets.length === 0 ||
      chartData.labels.length === 0
    ) {
      return null; // Não mostra gauge se não há dados
    }

    const gaugeData = {
      datasets: [
        {
          data: [atingimento, Math.max(0, 100 - atingimento)],
          backgroundColor: [progressColor, "#f0f0f0"],
          borderWidth: 0,
          cutout: "75%",
        },
      ],
    };

    const gaugeOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      rotation: -90,
      circumference: 180,
    };

    return (
      <div className={classes.gaugeSection}>
        <Tippy
          content={`
            ${t("whatshappening.labels.meta")}: ${formatNumber(metaVal, unit, showUnitBefore)} | 
            ${t("whatshappening.labels.real")}: ${formatNumber(realVal, unit, showUnitBefore)}
          `}
          theme="light"
          placement="left"
        >
          <div className={classes.gaugeBox}>
            <Typography className={classes.performanceLabel}>
              {t("whatshappening.labels.achievement")}
            </Typography>
            <div className={classes.gaugeContainer}>
              <Doughnut data={gaugeData} options={gaugeOptions} />
            </div>
            <Typography 
              className={classes.performanceValue}
              style={{ color: progressColor }}
            >
              {atingimento}%
            </Typography>
          </div>
        </Tippy>
      </div>
    );
  }

  // --- Renderização da Tabela Modernizada ---
  function renderTable() {
    const labels = filteredData?.labels || [];
    const series = filteredData?.series || [];
    const seriesNames = filteredData?.seriesNames || [];

    if (!labels.length || series.length < 2) {
      return renderEmptyState();
    }

    // Encontrar índices de "Meta" e "Real"
    const metaIndex = seriesNames.findIndex(name => name === "Meta");
    const realIndex = seriesNames.findIndex(name => name === "Real");
    
    // Usar índices padrão se não encontrar (compatibilidade com dados antigos)
    const metaIdx = metaIndex !== -1 ? metaIndex : 0;
    const realIdx = realIndex !== -1 ? realIndex : 1;

    const metaValues = series[metaIdx] || [];
    const realValues = series[realIdx] || [];
    const metaName = seriesNames[metaIdx] || t("whatshappening.labels.meta");
    const realName = seriesNames[realIdx] || t("whatshappening.labels.real");

    return (
      <div className={classes.tableContainer}>
        <table className={classes.kpiTable}>
          <thead>
            <tr>
              <th className={classes.kpiTableHeaderCell} />
              {labels.map((lbl, idx) => (
                <th key={idx} className={classes.kpiTableHeaderCell}>
                  {lbl}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={`${classes.kpiTableCell} ${classes.rowHeader}`}>
                {metaName}
              </td>
              {labels.map((_, i) => (
                <td key={i} className={classes.kpiTableCell}>
                  {metaValues[i] != null
                    ? formatNumber(metaValues[i], unit, showUnitBefore)
                    : "--"}
                </td>
              ))}
            </tr>
            <tr>
              <td className={`${classes.kpiTableCell} ${classes.rowHeader}`}>
                {realName}
              </td>
              {labels.map((_, i) => (
                <td key={i} className={classes.kpiTableCell}>
                  {realValues[i] != null
                    ? formatNumber(realValues[i], unit, showUnitBefore)
                    : "--"}
                </td>
              ))}
            </tr>
            <tr>
              <td className={`${classes.kpiTableCell} ${classes.rowHeader}`}>
                {t("whatshappening.labels.comparison_goal_percent")}
              </td>
              {labels.map((_, i) => {
                const mv = metaValues[i];
                const rv = realValues[i];
                let comparisonRatio = null;

                if (mv != null && mv > 0 && rv != null) {
                  comparisonRatio = rv / mv;
                }

                if (comparisonRatio !== null) {
                  const ratioPercent = (comparisonRatio * 100).toFixed(1);
                  const baseColor = getProgressColor(
                    comparisonRatio,
                    trackingMode,
                    thresholdsConfig
                  );
                  const bgColor = pastelColor(baseColor);
                  return (
                    <td
                      key={i}
                      style={{ 
                        backgroundColor: bgColor,
                        fontWeight: "600",
                        color: "#374151"
                      }}
                      className={classes.kpiTableCell}
                    >
                      {ratioPercent}%
                    </td>
                  );
                }
                return (
                  <td key={i} className={classes.kpiTableCell}>
                    --
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // --- Renderização Principal ---
  return (
    <div className={classes.container}>
      {renderFilters()}
      <div className={classes.contentWrapper}>
        {viewMode === "chart" ? renderChart() : renderTable()}
        {renderGauge()}
      </div>
    </div>
  );
}

// --- PropTypes ---
MixedBarLineChart.propTypes = {
  timeAgg: PropTypes.string.isRequired,
  viewMode: PropTypes.string.isRequired,
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    series: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    seriesNames: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  trackingMode: PropTypes.string.isRequired,
  thresholdsConfig: PropTypes.object.isRequired,
  unit: PropTypes.string,
  showUnitBefore: PropTypes.bool,
  rawData: PropTypes.arrayOf(PropTypes.shape({
    branch: PropTypes.string,
    workfront: PropTypes.string,
  })),
  showFilters: PropTypes.bool, // Prop opcional para exibir filtros
  selectedBranch: PropTypes.string, // Valor do filtro de branch (controlado externamente)
  selectedWorkfront: PropTypes.string, // Valor do filtro de workfront (controlado externamente)
  onBranchChange: PropTypes.func, // Callback quando o filtro de branch muda
  onWorkfrontChange: PropTypes.func, // Callback quando o filtro de workfront muda
};

export default React.memo(MixedBarLineChart);
